/**
 * Regulatory filing engine — Form PF field model + deterministic auto-population
 * (Master Plan Argus Phase 3: "Deadline-driven filings, AI draft → filing-ready").
 *
 * Pure and reference-data-injected (no DB, no `new Date()`): given the canonical
 * Fund / LP / Commitment data Argus already holds, compute every field Argus can
 * KNOW, mark the fields it cannot (regulatory AUM, audited GAV/NAV,
 * portfolio-company data) as gaps, and flag the fields a human or Claude must
 * draft. The field MODEL lives here, not in the schema (ADR-0006), so Form ADV /
 * AIFMD reuse the same `filings` substrate by adding a model, not a migration.
 *
 * Three provenance classes drive the product's honesty mechanic:
 *   auto   — derived deterministically from platform data (this module computes it)
 *   ai     — Claude drafts it; a human accepts it (engine leaves it pending)
 *   manual — Argus has no basis; flagged as a gap for the CCO to enter
 *
 * IMPORTANT: this is filing-PREPARATION assistance, never authoritative. Every
 * value is reviewable and the package is "filing-ready", not auto-filed — SEC
 * rails (PFRD/IARD) do not broadly allow third-party submission (build doc §1.4).
 */

import type { CanonicalCommitment, CanonicalFund, CanonicalLp } from "./canonical";

export const FORM_PF_MODEL_VERSION = "form-pf-v1.0";
export const FORM_ADV_MODEL_VERSION = "form-adv-v1.0";

export type FilingFormType = "form_pf" | "form_adv";

export type FormPfSection = "section_1a" | "section_1b" | "section_4";

/**
 * Form ADV Part 1A sections Argus models. Item 1 (identifying info) and Item 5
 * (advisory business) are firm-scoped; Schedule D Section 7.B.1 (private fund
 * reporting) is per-fund and the part Argus's fund data maps onto directly.
 */
export type FormAdvSection = "adv_item_1" | "adv_item_5" | "adv_7b1";

/** Any section across the filing models. Field/value rows carry it as text. */
export type FilingSection = FormPfSection | FormAdvSection;

export type FieldDerivation = "auto" | "ai" | "manual";

export type FieldType =
  | "text"
  | "longtext"
  | "integer"
  | "currency"
  | "percent"
  | "boolean"
  | "enum"
  | "composition";

/**
 * Form PF "type of private fund" (Question 1b). A private credit / infrastructure
 * fund reports as a private equity fund on Form PF; fund-of-funds / other map to
 * "other private fund".
 */
export type FormPfFundType =
  | "hedge_fund"
  | "liquidity_fund"
  | "private_equity"
  | "real_estate"
  | "securitized_asset"
  | "venture_capital"
  | "other";

/** A field definition in the model. Per-fund fields are templates expanded once
 * per fund with a fund-suffixed key. */
export interface FormPfFieldDef {
  /** Stable key. Firm fields: literal. Fund fields: `${templateKey}` (expanded
   * to `${templateKey}__${fundId}` by {@link buildFormPfFields}). */
  key: string;
  section: FilingSection;
  scope: "firm" | "fund";
  /** Form item/question reference, shown as provenance in the UI. */
  item: string;
  label: string;
  type: FieldType;
  derivation: FieldDerivation;
  help?: string;
  /** For `enum` fields. */
  options?: readonly string[];
  regRef?: string;
}

/** A filing field definition (form-agnostic). Alias of {@link FormPfFieldDef};
 * the Form ADV model reuses the same shape with its own sections. */
export type FilingFieldDef = FormPfFieldDef;

/* -------------------------------------------------------------- field model */

const SECTION_1A: readonly FormPfFieldDef[] = [
  {
    key: "adviser_name",
    section: "section_1a",
    scope: "firm",
    item: "Item A.1",
    label: "Name of the filing adviser",
    type: "text",
    derivation: "auto",
    regRef: "Form PF Item A",
  },
  {
    key: "crd_number",
    section: "section_1a",
    scope: "firm",
    item: "Item A.2",
    label: "Adviser SEC 801- / CRD number",
    type: "text",
    derivation: "manual",
    help: "Argus does not store your IARD/CRD identifiers — enter them once.",
  },
  {
    key: "aggregate_raum",
    section: "section_1a",
    scope: "firm",
    item: "Item B.1",
    label: "Aggregate regulatory assets under management",
    type: "currency",
    derivation: "manual",
    help: "Regulatory AUM is a Form ADV-reconciled figure Argus does not compute. Enter the value as of your fiscal year-end.",
    regRef: "Form PF Item B / Form ADV Item 5.F",
  },
  {
    key: "total_private_funds",
    section: "section_1a",
    scope: "firm",
    item: "Item B.2",
    label: "Total number of private funds reported",
    type: "integer",
    derivation: "auto",
    help: "Counted from the funds in Argus. Confirm every reportable fund is present.",
  },
  {
    key: "total_private_fund_assets",
    section: "section_1a",
    scope: "firm",
    item: "Item B.3",
    label: "Aggregate private fund assets (from recorded fund sizes)",
    type: "currency",
    derivation: "auto",
    help: "Sum of recorded fund sizes — a starting figure to reconcile against audited gross asset value.",
  },
  {
    key: "total_capital_commitments",
    section: "section_1a",
    scope: "firm",
    item: "Item B.4",
    label: "Aggregate limited-partner capital commitments",
    type: "currency",
    derivation: "auto",
    help: "Sum of active LP commitments across all funds in Argus.",
  },
  {
    key: "filer_determination",
    section: "section_1a",
    scope: "firm",
    item: "Threshold",
    label: "Large private equity adviser? (Section 4 applicability)",
    type: "longtext",
    derivation: "ai",
    help: "Drafted from your fund mix. Section 4 applies to advisers with ≥ $2B in private equity regulatory AUM — confirm against your regulatory AUM.",
    regRef: "Form PF Instruction 3 / Section 4",
  },
  {
    key: "cover_memo",
    section: "section_1a",
    scope: "firm",
    item: "Preparer memo",
    label: "Preparer's filing memo",
    type: "longtext",
    derivation: "ai",
    help: "A reviewable narrative summary of filer type, period, applicable sections, and outstanding gaps. Not part of the SEC form.",
  },
];

const SECTION_1B_TEMPLATE: readonly FormPfFieldDef[] = [
  {
    key: "fund_name",
    section: "section_1b",
    scope: "fund",
    item: "Question 1(b)",
    label: "Name of the private fund",
    type: "text",
    derivation: "auto",
  },
  {
    key: "fund_state_country",
    section: "section_1b",
    scope: "fund",
    item: "Question 3",
    label: "State / country where the fund is organized",
    type: "text",
    derivation: "auto",
    help: "From the fund's recorded domicile.",
  },
  {
    key: "fund_type",
    section: "section_1b",
    scope: "fund",
    item: "Question 1(b)",
    label: "Type of private fund",
    type: "enum",
    derivation: "ai",
    options: [
      "hedge_fund",
      "liquidity_fund",
      "private_equity",
      "real_estate",
      "securitized_asset",
      "venture_capital",
      "other",
    ],
    help: "Classified from the fund's strategy; confirm the Form PF fund-type characterization.",
  },
  {
    key: "fund_gross_asset_value",
    section: "section_1b",
    scope: "fund",
    item: "Question 9",
    label: "Gross asset value (GAV)",
    type: "currency",
    derivation: "manual",
    help: "Audited GAV is not stored in Argus — enter the value as of the reporting date.",
  },
  {
    key: "fund_net_asset_value",
    section: "section_1b",
    scope: "fund",
    item: "Question 9",
    label: "Net asset value (NAV)",
    type: "currency",
    derivation: "manual",
    help: "Audited NAV is not stored in Argus — enter the value as of the reporting date.",
  },
  {
    key: "fund_capital_commitments",
    section: "section_1b",
    scope: "fund",
    item: "Question 8",
    label: "Aggregate capital commitments to the fund",
    type: "currency",
    derivation: "auto",
    help: "Sum of active LP commitments to this fund in Argus.",
  },
  {
    key: "fund_beneficial_owners",
    section: "section_1b",
    scope: "fund",
    item: "Question 15",
    label: "Approximate number of beneficial owners",
    type: "integer",
    derivation: "auto",
    help: "Count of LPs with an active commitment to this fund. Confirm against the fund's beneficial-owner register.",
  },
  {
    key: "fund_us_person_pct",
    section: "section_1b",
    scope: "fund",
    item: "Question 16",
    label: "Percentage owned by United States persons",
    type: "percent",
    derivation: "manual",
    help: "US-person status is a legal determination Argus does not infer from LP domicile — confirm and enter.",
  },
  {
    key: "fund_master_feeder",
    section: "section_1b",
    scope: "fund",
    item: "Question 5",
    label: "Is the fund part of a master-feeder arrangement?",
    type: "boolean",
    derivation: "ai",
    help: "Suggested from the fund's name and structure; confirm.",
  },
  {
    key: "fund_investor_composition",
    section: "section_1b",
    scope: "fund",
    item: "Question 16",
    label: "Beneficial-owner composition by investor type",
    type: "composition",
    derivation: "auto",
    help: "Commitment-weighted breakdown by LP type from Argus.",
  },
];

const SECTION_4_TEMPLATE: readonly FormPfFieldDef[] = [
  {
    key: "pe_portfolio_company_count",
    section: "section_4",
    scope: "fund",
    item: "Question 30",
    label: "Number of portfolio companies",
    type: "integer",
    derivation: "manual",
    help: "Portfolio-company data is not held in Argus (it lives in the deal/portfolio system — e.g. Atlas). Enter or import.",
    regRef: "Form PF Section 4",
  },
  {
    key: "pe_event_of_default",
    section: "section_4",
    scope: "fund",
    item: "Question 31",
    label: "Did any controlled portfolio company experience an event of default?",
    type: "boolean",
    derivation: "manual",
    help: "Requires portfolio-company financials Argus does not hold.",
    regRef: "Form PF Section 4",
  },
  {
    key: "pe_bridge_financing",
    section: "section_4",
    scope: "fund",
    item: "Question 32",
    label: "Aggregate bridge financing to controlled portfolio companies",
    type: "currency",
    derivation: "manual",
    help: "Requires portfolio-company financing data Argus does not hold.",
    regRef: "Form PF Section 4",
  },
  {
    key: "pe_industry_exposure",
    section: "section_4",
    scope: "fund",
    item: "Question 33",
    label: "Greatest industry exposure of controlled portfolio companies",
    type: "longtext",
    derivation: "manual",
    help: "Requires portfolio-company industry classification Argus does not hold.",
    regRef: "Form PF Section 4",
  },
];

/** Deterministic Form PF fund-type from the canonical strategy. This is both the
 * AI field's fallback and the eval target — a credit/infra fund reports as PE. */
export function classifyFundType(fund: Pick<CanonicalFund, "strategy">): FormPfFundType {
  switch (fund.strategy) {
    case "buyout":
    case "growth":
    case "credit":
    case "infrastructure":
      return "private_equity";
    case "venture":
      return "venture_capital";
    case "real_estate":
      return "real_estate";
    case "fund_of_funds":
    case "other":
    case null:
    case undefined:
      return "other";
    default:
      return "other";
  }
}

/** Heuristic master-feeder suggestion (the AI field's deterministic fallback). */
export function suggestMasterFeeder(fund: Pick<CanonicalFund, "name" | "shortName">): boolean {
  const hay = `${fund.name} ${fund.shortName ?? ""}`.toLowerCase();
  return /\b(master|feeder|onshore|offshore|parallel)\b/.test(hay);
}

/** Expand the model into concrete fields for a given set of funds. Per-fund
 * templates become `${templateKey}__${fundId}`; firm fields pass through. */
export function buildFormPfFields(
  funds: Pick<CanonicalFund, "id">[],
): (FormPfFieldDef & { fundId: string | null })[] {
  const out: (FormPfFieldDef & { fundId: string | null })[] = [];
  for (const f of SECTION_1A) out.push({ ...f, fundId: null });
  for (const fund of funds) {
    for (const t of SECTION_1B_TEMPLATE) {
      out.push({ ...t, key: `${t.key}__${fund.id}`, fundId: fund.id });
    }
  }
  for (const fund of funds) {
    for (const t of SECTION_4_TEMPLATE) {
      out.push({ ...t, key: `${t.key}__${fund.id}`, fundId: fund.id });
    }
  }
  return out;
}

/* ------------------------------------------------------------ auto-populate */

export interface InvestorCompositionEntry {
  type: string;
  amount: number;
  pct: number;
}

/** A computed field value, ready to persist into `filing_field_values`. */
export interface FieldValueDraft {
  fieldKey: string;
  section: FilingSection;
  source: FieldDerivation;
  value: string | number | boolean | InvestorCompositionEntry[] | null;
  isGap: boolean;
  confidence: number | null;
  citation: string | null;
}

export interface AutoPopulateInput {
  firmName: string;
  funds: CanonicalFund[];
  lps: CanonicalLp[];
  commitments: CanonicalCommitment[];
}

const ACTIVE = (c: CanonicalCommitment): boolean => c.status === "active";

function fundCommitmentTotal(commitments: CanonicalCommitment[], fundId: string): number {
  return commitments
    .filter((c) => ACTIVE(c) && c.fundId === fundId)
    .reduce((s, c) => s + c.amount, 0);
}

function fundBeneficialOwners(commitments: CanonicalCommitment[], fundId: string): number {
  return new Set(commitments.filter((c) => ACTIVE(c) && c.fundId === fundId).map((c) => c.lpId))
    .size;
}

/** Commitment-weighted composition by LP type for a fund, descending by amount. */
export function investorComposition(
  fundId: string,
  lps: CanonicalLp[],
  commitments: CanonicalCommitment[],
): InvestorCompositionEntry[] {
  const lpType = new Map(lps.map((l) => [l.id, l.type ?? "other"]));
  const byType = new Map<string, number>();
  let total = 0;
  for (const c of commitments) {
    if (!ACTIVE(c) || c.fundId !== fundId) continue;
    const t = lpType.get(c.lpId) ?? "other";
    byType.set(t, (byType.get(t) ?? 0) + c.amount);
    total += c.amount;
  }
  if (total === 0) return [];
  return [...byType.entries()]
    .map(([type, amount]) => ({ type, amount, pct: Math.round((amount / total) * 1000) / 10 }))
    .sort((a, b) => b.amount - a.amount);
}

/**
 * Produce the initial field-value drafts for a Form PF filing:
 *   - `auto` fields are computed (or flagged as gaps when the datum is missing);
 *   - `ai` fields are left pending (value null, source 'ai', not a gap) for the
 *     draft-filing edge function to fill — with a deterministic suggestion baked
 *     into the citation so the field is never empty in preview/offline mode;
 *   - `manual` fields are flagged as gaps for the CCO to enter.
 */
export function autoPopulateFormPf(input: AutoPopulateInput): FieldValueDraft[] {
  const { firmName, funds, lps, commitments } = input;
  const fundById = new Map(funds.map((f) => [f.id, f]));
  const out: FieldValueDraft[] = [];

  const fields = buildFormPfFields(funds);
  for (const f of fields) {
    if (f.derivation === "manual") {
      out.push({
        fieldKey: f.key,
        section: f.section,
        source: "manual",
        value: null,
        isGap: true,
        confidence: null,
        citation: null,
      });
      continue;
    }
    if (f.derivation === "ai") {
      // Pending AI draft. Deterministic suggestion travels in the citation so the
      // workspace shows a value even before (or without) a live model call.
      let suggestion: string | null = null;
      if (f.fundId) {
        const fund = fundById.get(f.fundId);
        if (fund && f.key.startsWith("fund_type__"))
          suggestion = `suggested: ${classifyFundType(fund)}`;
        if (fund && f.key.startsWith("fund_master_feeder__"))
          suggestion = `suggested: ${suggestMasterFeeder(fund) ? "yes" : "no"}`;
      }
      out.push({
        fieldKey: f.key,
        section: f.section,
        source: "ai",
        value: null,
        isGap: false,
        confidence: null,
        citation: suggestion,
      });
      continue;
    }

    // auto
    const v = computeAutoField(f, { firmName, funds, lps, commitments, fundById });
    out.push({
      fieldKey: f.key,
      section: f.section,
      source: "auto",
      value: v.value,
      isGap: v.value === null,
      confidence: v.value === null ? null : 1,
      citation: v.citation,
    });
  }
  return out;
}

function computeAutoField(
  f: FormPfFieldDef & { fundId: string | null },
  ctx: {
    firmName: string;
    funds: CanonicalFund[];
    lps: CanonicalLp[];
    commitments: CanonicalCommitment[];
    fundById: Map<string, CanonicalFund>;
  },
): { value: FieldValueDraft["value"]; citation: string | null } {
  const { firmName, funds, lps, commitments, fundById } = ctx;

  // Firm-level auto fields.
  switch (f.key) {
    case "adviser_name":
      return { value: firmName || null, citation: "Tenant / firm name" };
    case "total_private_funds":
      return { value: funds.length, citation: `${funds.length} fund(s) in Argus` };
    case "total_private_fund_assets": {
      const sized = funds.filter((x) => x.size != null);
      if (sized.length === 0) return { value: null, citation: null };
      const sum = sized.reduce((s, x) => s + (x.size ?? 0), 0);
      return {
        value: sum,
        citation:
          sized.length === funds.length
            ? `Sum of ${funds.length} recorded fund size(s)`
            : `Sum of ${sized.length}/${funds.length} funds with a recorded size`,
      };
    }
    case "total_capital_commitments": {
      const active = commitments.filter(ACTIVE);
      if (active.length === 0) return { value: null, citation: null };
      const sum = active.reduce((s, c) => s + c.amount, 0);
      return { value: sum, citation: `Sum of ${active.length} active commitment(s)` };
    }
  }

  // Per-fund auto fields (key is `${template}__${fundId}`).
  const fundId = f.fundId;
  if (!fundId) return { value: null, citation: null };
  const fund = fundById.get(fundId);
  if (!fund) return { value: null, citation: null };

  if (f.key.startsWith("fund_name__")) return { value: fund.name, citation: "Fund record" };
  if (f.key.startsWith("fund_state_country__"))
    return fund.domicile
      ? { value: fund.domicile, citation: "Fund domicile" }
      : { value: null, citation: null };
  if (f.key.startsWith("fund_capital_commitments__")) {
    const total = fundCommitmentTotal(commitments, fundId);
    return total > 0
      ? { value: total, citation: `${fund.name} active commitments` }
      : { value: null, citation: null };
  }
  if (f.key.startsWith("fund_beneficial_owners__")) {
    const n = fundBeneficialOwners(commitments, fundId);
    return n > 0
      ? { value: n, citation: `${n} LP(s) with active commitments` }
      : { value: null, citation: null };
  }
  if (f.key.startsWith("fund_investor_composition__")) {
    const comp = investorComposition(fundId, lps, commitments);
    return comp.length > 0
      ? { value: comp, citation: `${comp.length} investor type(s)` }
      : { value: null, citation: null };
  }
  return { value: null, citation: null };
}

/* -------------------------------------------------------------- completeness */

export interface FilingCompleteness {
  total: number;
  /** auto/ai/manual values with a non-null value AND a human review of accepted/edited. */
  reviewed: number;
  /** values populated (non-null), regardless of review. */
  populated: number;
  auto: number;
  ai: number;
  manual: number;
  gaps: number;
  /** ai fields still awaiting a draft (value null). */
  aiPending: number;
  /** % of fields populated. */
  pct: number;
  /** % of fields a human has signed off (accepted or edited). */
  reviewedPct: number;
  bySection: Record<string, { total: number; populated: number; gaps: number }>;
}

export interface FieldValueState {
  section: string;
  source: FieldDerivation;
  value: unknown;
  isGap: boolean;
  reviewStatus: "unreviewed" | "accepted" | "edited" | "flagged";
}

export function filingCompleteness(fields: FieldValueState[]): FilingCompleteness {
  const total = fields.length;
  let populated = 0;
  let reviewed = 0;
  let auto = 0;
  let ai = 0;
  let manual = 0;
  let gaps = 0;
  let aiPending = 0;
  const bySection: FilingCompleteness["bySection"] = {};

  for (const f of fields) {
    const hasValue = f.value !== null && f.value !== undefined && f.value !== "";
    if (hasValue) populated += 1;
    if (f.reviewStatus === "accepted" || f.reviewStatus === "edited") reviewed += 1;
    if (f.source === "auto") auto += 1;
    if (f.source === "ai") ai += 1;
    if (f.source === "manual") manual += 1;
    if (f.isGap && !hasValue) gaps += 1;
    if (f.source === "ai" && !hasValue) aiPending += 1;

    const s = (bySection[f.section] ??= { total: 0, populated: 0, gaps: 0 });
    s.total += 1;
    if (hasValue) s.populated += 1;
    if (f.isGap && !hasValue) s.gaps += 1;
  }

  return {
    total,
    reviewed,
    populated,
    auto,
    ai,
    manual,
    gaps,
    aiPending,
    pct: total === 0 ? 0 : Math.round((populated / total) * 100),
    reviewedPct: total === 0 ? 0 : Math.round((reviewed / total) * 100),
    bySection,
  };
}

/** Whether a filing is ready to package: every populated field is human-reviewed
 * and no gaps remain unaddressed (gaps may be explicitly marked N/A via a value). */
export function isFilingReady(fields: FieldValueState[]): boolean {
  if (fields.length === 0) return false;
  return fields.every((f) => {
    const hasValue = f.value !== null && f.value !== undefined && f.value !== "";
    if (!hasValue) return false;
    return f.reviewStatus === "accepted" || f.reviewStatus === "edited";
  });
}

/* ---------------------------------------------------- deterministic AI drafts */

export interface DeterministicDraft {
  fieldKey: string;
  section: FilingSection;
  value: string | boolean;
  confidence: number;
  citation: string;
}

export interface DeterministicDraftInput {
  firmName: string;
  period: string;
  funds: CanonicalFund[];
  commitments: CanonicalCommitment[];
  /** The `ai`-source field keys present on the filing (e.g. `fund_type__<id>`, `cover_memo`). */
  aiFieldKeys: string[];
}

function usdCompact(n: number): string {
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  return `$${n.toLocaleString("en-US")}`;
}

/**
 * Deterministic drafts for a Form PF filing's `ai` fields — the offline / no-key
 * fallback the draft-filing edge function also computes server-side. Returns a
 * value for every `ai` field key present so "Draft with AI" always completes,
 * even when the edge function or model is unavailable. Drafts are suggestions for
 * human sign-off, never authoritative (build doc §1.4). The live model only
 * enriches the narratives and justifies the classifications on top of these.
 */
export function deterministicFormPfDrafts(input: DeterministicDraftInput): DeterministicDraft[] {
  const { firmName, period, funds, commitments, aiFieldKeys } = input;
  const keys = new Set(aiFieldKeys);
  const out: DeterministicDraft[] = [];

  const totalAssets = funds.reduce((s, f) => s + (f.size ?? 0), 0);
  const totalCommit = commitments.filter(ACTIVE).reduce((s, c) => s + c.amount, 0);
  const peFunds = funds.filter((f) => classifyFundType(f) === "private_equity");

  for (const f of funds) {
    const typeKey = `fund_type__${f.id}`;
    if (keys.has(typeKey)) {
      out.push({
        fieldKey: typeKey,
        section: "section_1b",
        value: classifyFundType(f),
        confidence: 0.9,
        citation: `Strategy "${f.strategy ?? "unspecified"}" maps to this Form PF fund type.`,
      });
    }
    const mfKey = `fund_master_feeder__${f.id}`;
    if (keys.has(mfKey)) {
      const mf = suggestMasterFeeder(f);
      out.push({
        fieldKey: mfKey,
        section: "section_1b",
        value: mf,
        confidence: mf ? 0.7 : 0.6,
        citation: mf
          ? "Fund name suggests a master-feeder / parallel structure — confirm."
          : "No master-feeder indicator in the fund name — confirm.",
      });
    }
  }

  if (keys.has("filer_determination")) {
    const likelyLarge = totalAssets >= 2e9;
    out.push({
      fieldKey: "filer_determination",
      section: "section_1a",
      value:
        `${firmName} manages ${funds.length} private fund(s) totalling ${usdCompact(totalAssets)} in recorded assets, of which ${peFunds.length} are private-equity-type. ` +
        `Section 1 applies to all Form PF filers. Section 4 (large private equity adviser) applies only if private-equity regulatory AUM is at least $2 billion — ` +
        `${likelyLarge ? "recorded assets exceed that threshold, so Section 4 likely applies." : "recorded assets are below that threshold, but confirm against regulatory AUM (which Argus does not compute)."}`,
      confidence: 0.65,
      citation: "Derived from recorded fund sizes; reconcile to Form ADV regulatory AUM.",
    });
  }

  if (keys.has("cover_memo")) {
    out.push({
      fieldKey: "cover_memo",
      section: "section_1a",
      value:
        `Form PF — ${period} preparer memo for ${firmName}.\n\n` +
        `Scope: ${funds.length} private fund(s); aggregate recorded assets ${usdCompact(totalAssets)}; aggregate LP commitments ${usdCompact(totalCommit)}.\n` +
        `Argus auto-populated fund identity, domicile, commitments, and beneficial-owner counts from the fund and LP records. ` +
        `Gross/net asset value, regulatory AUM, US-person percentages, and Section 4 portfolio-company data are not held in Argus and are flagged as gaps for entry. ` +
        `Every field requires CCO review before this package is filed via PFRD.`,
      confidence: 0.6,
      citation: "Templated from the filing's auto-populated figures.",
    });
  }

  return out;
}

/* ============================================================ FORM ADV ====
 * Form ADV Part 1A — Item 1 (identifying info) + Item 5 (advisory business),
 * firm-scoped; Schedule D Section 7.B.1 (private fund reporting), per-fund.
 * Reuses the same FieldValueDraft substrate, provenance classes, and leaf
 * helpers as Form PF (ADR-0006: a new MODEL, not a new migration). The fund
 * type uses the same enum as Form PF (Form PF derives its categories from ADV).
 * ===========================================================================*/

const ADV_ITEM_1: readonly FilingFieldDef[] = [
  {
    key: "adviser_name",
    section: "adv_item_1",
    scope: "firm",
    item: "Item 1.A",
    label: "Name of the adviser (legal entity)",
    type: "text",
    derivation: "auto",
    regRef: "Form ADV Part 1A Item 1",
  },
  {
    key: "adviser_crd",
    section: "adv_item_1",
    scope: "firm",
    item: "Item 1.D",
    label: "SEC 801- / CRD number",
    type: "text",
    derivation: "manual",
    help: "Argus does not store your IARD/CRD identifiers — enter them once.",
  },
  {
    key: "principal_office",
    section: "adv_item_1",
    scope: "firm",
    item: "Item 1.F",
    label: "Principal office and place of business",
    type: "text",
    derivation: "manual",
    help: "Your principal business address as filed on IARD.",
  },
];

const ADV_ITEM_5: readonly FilingFieldDef[] = [
  {
    key: "regulatory_aum",
    section: "adv_item_5",
    scope: "firm",
    item: "Item 5.F(2)",
    label: "Regulatory assets under management",
    type: "currency",
    derivation: "manual",
    help: "Regulatory AUM is computed per the Form ADV instructions (gross, with no deduction for leverage) — Argus does not derive it. Enter the value as of your most recent fiscal year-end.",
    regRef: "Form ADV Item 5.F",
  },
  {
    key: "num_private_funds",
    section: "adv_item_5",
    scope: "firm",
    item: "Item 5.D / 7.B",
    label: "Number of private funds advised",
    type: "integer",
    derivation: "auto",
    help: "Counted from the funds in Argus. Confirm every reportable private fund is present.",
  },
  {
    key: "aggregate_private_fund_assets",
    section: "adv_item_5",
    scope: "firm",
    item: "Item 5.D",
    label: "Aggregate private fund assets (from recorded fund sizes)",
    type: "currency",
    derivation: "auto",
    help: "Sum of recorded fund sizes — a starting figure to reconcile against gross asset value / regulatory AUM.",
  },
  {
    key: "num_employees",
    section: "adv_item_5",
    scope: "firm",
    item: "Item 5.A",
    label: "Approximate number of employees",
    type: "integer",
    derivation: "manual",
    help: "Headcount is not held in Argus — enter your approximate employee count.",
  },
  {
    key: "advisory_business_summary",
    section: "adv_item_5",
    scope: "firm",
    item: "Item 5 (narrative)",
    label: "Advisory business summary",
    type: "longtext",
    derivation: "ai",
    help: "A reviewable narrative describing the advisory business and private-fund scope. Drafted from your fund data; not a substitute for the Form ADV Part 2A brochure.",
  },
];

const ADV_7B1_TEMPLATE: readonly FilingFieldDef[] = [
  {
    key: "adv_fund_name",
    section: "adv_7b1",
    scope: "fund",
    item: "7.B.1 Q1",
    label: "Name of the private fund",
    type: "text",
    derivation: "auto",
  },
  {
    key: "adv_fund_pfid",
    section: "adv_7b1",
    scope: "fund",
    item: "7.B.1 Q2",
    label: "Private fund identification number (PFID)",
    type: "text",
    derivation: "manual",
    help: "The 1000-series PFID the SEC assigns each private fund — Argus does not store it.",
  },
  {
    key: "adv_fund_state_country",
    section: "adv_7b1",
    scope: "fund",
    item: "7.B.1 Q5",
    label: "State / country where the fund is organized",
    type: "text",
    derivation: "auto",
    help: "From the fund's recorded domicile.",
  },
  {
    key: "adv_fund_type",
    section: "adv_7b1",
    scope: "fund",
    item: "7.B.1 Q10",
    label: "Type of private fund",
    type: "enum",
    derivation: "ai",
    options: [
      "hedge_fund",
      "liquidity_fund",
      "private_equity",
      "real_estate",
      "securitized_asset",
      "venture_capital",
      "other",
    ],
    help: "Classified from the fund's strategy; confirm the Schedule D 7.B.1 fund-type characterization.",
  },
  {
    key: "adv_fund_master_feeder",
    section: "adv_7b1",
    scope: "fund",
    item: "7.B.1 Q6",
    label: "Is the fund part of a master-feeder arrangement?",
    type: "boolean",
    derivation: "ai",
    help: "Suggested from the fund's name / structure; confirm.",
  },
  {
    key: "adv_fund_gav",
    section: "adv_7b1",
    scope: "fund",
    item: "7.B.1 Q11",
    label: "Current gross asset value of the fund",
    type: "currency",
    derivation: "manual",
    help: "Gross asset value is a valuation figure Argus does not hold — enter it as of the reporting date.",
  },
  {
    key: "adv_fund_commitments",
    section: "adv_7b1",
    scope: "fund",
    item: "7.B.1 (ref)",
    label: "Aggregate capital commitments (reference)",
    type: "currency",
    derivation: "auto",
    help: "Sum of active LP commitments to this fund in Argus — a reference for reconciling GAV.",
  },
  {
    key: "adv_fund_num_owners",
    section: "adv_7b1",
    scope: "fund",
    item: "7.B.1 Q14",
    label: "Approximate number of beneficial owners",
    type: "integer",
    derivation: "auto",
    help: "Count of LPs with an active commitment to this fund. Confirm against the beneficial-owner register.",
  },
  {
    key: "adv_fund_min_investment",
    section: "adv_7b1",
    scope: "fund",
    item: "7.B.1 Q13",
    label: "Minimum investment commitment",
    type: "currency",
    derivation: "manual",
    help: "The minimum the fund requires of an investor — a term Argus does not record.",
  },
  {
    key: "adv_fund_pct_owned_adviser",
    section: "adv_7b1",
    scope: "fund",
    item: "7.B.1 Q15",
    label: "Percentage owned by the adviser and related persons",
    type: "percent",
    derivation: "manual",
    help: "GP / related-person ownership is a legal determination Argus does not infer.",
  },
  {
    key: "adv_fund_exemption",
    section: "adv_7b1",
    scope: "fund",
    item: "7.B.1 Q21",
    label: "Investment Company Act exclusion relied upon",
    type: "text",
    derivation: "manual",
    help: "Typically 3(c)(1) or 3(c)(7) — a legal determination for your counsel/CCO to confirm.",
  },
  {
    key: "adv_fund_auditor",
    section: "adv_7b1",
    scope: "fund",
    item: "7.B.1 Q23",
    label: "Is the fund audited? Name of the auditor",
    type: "text",
    derivation: "manual",
    help: "Audit status and auditor are not held in Argus — enter them.",
  },
];

/** Expand the Form ADV model into concrete fields for a set of funds. */
export function buildFormAdvFields(
  funds: Pick<CanonicalFund, "id">[],
): (FilingFieldDef & { fundId: string | null })[] {
  const out: (FilingFieldDef & { fundId: string | null })[] = [];
  for (const f of ADV_ITEM_1) out.push({ ...f, fundId: null });
  for (const f of ADV_ITEM_5) out.push({ ...f, fundId: null });
  for (const fund of funds) {
    for (const t of ADV_7B1_TEMPLATE) {
      out.push({ ...t, key: `${t.key}__${fund.id}`, fundId: fund.id });
    }
  }
  return out;
}

function computeAutoFieldAdv(
  f: FilingFieldDef & { fundId: string | null },
  ctx: {
    firmName: string;
    funds: CanonicalFund[];
    commitments: CanonicalCommitment[];
    fundById: Map<string, CanonicalFund>;
  },
): { value: FieldValueDraft["value"]; citation: string | null } {
  const { firmName, funds, commitments, fundById } = ctx;

  switch (f.key) {
    case "adviser_name":
      return { value: firmName || null, citation: "Tenant / firm name" };
    case "num_private_funds":
      return { value: funds.length, citation: `${funds.length} fund(s) in Argus` };
    case "aggregate_private_fund_assets": {
      const sized = funds.filter((x) => x.size != null);
      if (sized.length === 0) return { value: null, citation: null };
      const sum = sized.reduce((s, x) => s + (x.size ?? 0), 0);
      return {
        value: sum,
        citation:
          sized.length === funds.length
            ? `Sum of ${funds.length} recorded fund size(s)`
            : `Sum of ${sized.length}/${funds.length} funds with a recorded size`,
      };
    }
  }

  const fundId = f.fundId;
  if (!fundId) return { value: null, citation: null };
  const fund = fundById.get(fundId);
  if (!fund) return { value: null, citation: null };

  if (f.key.startsWith("adv_fund_name__")) return { value: fund.name, citation: "Fund record" };
  if (f.key.startsWith("adv_fund_state_country__"))
    return fund.domicile
      ? { value: fund.domicile, citation: "Fund domicile" }
      : { value: null, citation: null };
  if (f.key.startsWith("adv_fund_commitments__")) {
    const total = fundCommitmentTotal(commitments, fundId);
    return total > 0
      ? { value: total, citation: `${fund.name} active commitments` }
      : { value: null, citation: null };
  }
  if (f.key.startsWith("adv_fund_num_owners__")) {
    const n = fundBeneficialOwners(commitments, fundId);
    return n > 0
      ? { value: n, citation: `${n} LP(s) with active commitments` }
      : { value: null, citation: null };
  }
  return { value: null, citation: null };
}

/**
 * Produce the initial field-value drafts for a Form ADV Part 1A filing —
 * mirrors {@link autoPopulateFormPf}: `auto` computed (or gap-flagged when
 * missing), `ai` left pending with a deterministic suggestion in the citation,
 * `manual` flagged as a gap for the CCO.
 */
export function autoPopulateFormAdv(input: AutoPopulateInput): FieldValueDraft[] {
  const { firmName, funds, commitments } = input;
  const fundById = new Map(funds.map((f) => [f.id, f]));
  const out: FieldValueDraft[] = [];

  for (const f of buildFormAdvFields(funds)) {
    if (f.derivation === "manual") {
      out.push({
        fieldKey: f.key,
        section: f.section,
        source: "manual",
        value: null,
        isGap: true,
        confidence: null,
        citation: null,
      });
      continue;
    }
    if (f.derivation === "ai") {
      let suggestion: string | null = null;
      if (f.fundId) {
        const fund = fundById.get(f.fundId);
        if (fund && f.key.startsWith("adv_fund_type__"))
          suggestion = `suggested: ${classifyFundType(fund)}`;
        if (fund && f.key.startsWith("adv_fund_master_feeder__"))
          suggestion = `suggested: ${suggestMasterFeeder(fund) ? "yes" : "no"}`;
      }
      out.push({
        fieldKey: f.key,
        section: f.section,
        source: "ai",
        value: null,
        isGap: false,
        confidence: null,
        citation: suggestion,
      });
      continue;
    }

    const v = computeAutoFieldAdv(f, { firmName, funds, commitments, fundById });
    out.push({
      fieldKey: f.key,
      section: f.section,
      source: "auto",
      value: v.value,
      isGap: v.value === null,
      confidence: v.value === null ? null : 1,
      citation: v.citation,
    });
  }
  return out;
}

/**
 * Deterministic drafts for a Form ADV filing's `ai` fields — the offline / no-key
 * fallback the draft-filing edge function also computes. Returns a value for every
 * `ai` field key present so "Draft with AI" always completes. Suggestions for
 * human sign-off, never authoritative (build doc §1.4).
 */
export function deterministicFormAdvDrafts(input: DeterministicDraftInput): DeterministicDraft[] {
  const { firmName, period, funds, commitments, aiFieldKeys } = input;
  const keys = new Set(aiFieldKeys);
  const out: DeterministicDraft[] = [];

  const totalAssets = funds.reduce((s, f) => s + (f.size ?? 0), 0);
  const totalCommit = commitments.filter(ACTIVE).reduce((s, c) => s + c.amount, 0);

  for (const f of funds) {
    const typeKey = `adv_fund_type__${f.id}`;
    if (keys.has(typeKey)) {
      out.push({
        fieldKey: typeKey,
        section: "adv_7b1",
        value: classifyFundType(f),
        confidence: 0.9,
        citation: `Strategy "${f.strategy ?? "unspecified"}" maps to this Schedule D 7.B.1 fund type.`,
      });
    }
    const mfKey = `adv_fund_master_feeder__${f.id}`;
    if (keys.has(mfKey)) {
      const mf = suggestMasterFeeder(f);
      out.push({
        fieldKey: mfKey,
        section: "adv_7b1",
        value: mf,
        confidence: mf ? 0.7 : 0.6,
        citation: mf
          ? "Fund name suggests a master-feeder / parallel structure — confirm."
          : "No master-feeder indicator in the fund name — confirm.",
      });
    }
  }

  if (keys.has("advisory_business_summary")) {
    out.push({
      fieldKey: "advisory_business_summary",
      section: "adv_item_5",
      value:
        `${firmName} is a private fund adviser managing ${funds.length} private fund(s) with ${usdCompact(totalAssets)} in recorded assets and ${usdCompact(totalCommit)} in aggregate LP commitments${period ? ` as of ${period}` : ""}. ` +
        `The adviser provides discretionary investment advice to pooled investment vehicles. ` +
        `Argus auto-populated fund identity, domicile, commitments, and beneficial-owner counts from the fund and LP records; regulatory AUM, gross asset value, employee count, ownership percentages, and Section 7.B.1 legal determinations are flagged as gaps for the CCO. ` +
        `This summary is a drafting aid and does not replace the Form ADV Part 2A brochure; every field requires CCO review before filing via IARD.`,
      confidence: 0.6,
      citation: "Templated from the filing's auto-populated figures.",
    });
  }

  return out;
}
